import {Form, formsStore, Record} from '../../../stores/Form';
import {exitSubForm, getFormStatuses, getSavedSearchesAction} from '../../Form/actions/form';
import {addNewStandAloneForm, updateCurrentRecordField} from '../../Form/actions/standAloneForm';
import {
    ADDTODLVTRACKITEMS,
    ADPFetchMax,
    ADVDISTRDATA,
    DLVTRACK,
    DLVTRACKI,
    DLVTRACKS,
    DOCGUID,
    LOADADVDISTRDATANOI,
    MOVEDLVTRACKITEMS,
    STATUS_FIELD,
    ADDDOCSTODELPLAN,
    UPDDELIVERYORDER
} from '../constants';
import {
    buildDataUrlFromParams,
    defaultUpdateFunction,
    runProcedure
} from '../../../services/webSdk/proc';
import {
    EditField,
    InputFields,
    InputFieldsReturn,
    InputProcData,
    MessageProcData,
    ProcBaseData,
    ProcData
} from 'priority-web-sdk';
import {
    showConfirmMessage,
    showErrorMessage,
    showInfoMessage
} from '../../../_shared/Messages/actions/MessageActions';
import messagesText from '../../../_shared/Messages/texts';
import {getLocalizedText, texts} from '../../../constants/TextConst';
import {addAndApplyFilters, applyFilters} from '../../Form/actions/filters';
import {priority} from '@priority-software/priority-proto-api/lib';
import {RouteType} from '../types';
import {startProcedure} from '../../../services/webSdk/service';
import {startAndFetchSubform} from '../../Form/actions/batchedActions/standAloneFormBatch';
import {buildAddress, mappedColumnIds} from '../utils';
import {getUuid4} from '../../../utils/getUuid4';
import {endProc} from '../../../_shared/GWTBridge/invoke/procMain';
import {undoAll} from '../../Form/actions/undo';

export type ResponseADP = {
    planningForm: Form | null;
    routesForm: Form | null;
};

export const cleanNumberString = (str: string) => {
    return str.replace(/[.,]/g, '');
};

export const fetchSubform = async (form: Form, name: string, hiddenFields: string[] = []) => {
    await startAndFetchSubform(form, name, 0, hiddenFields);
    return form.activeSubForm;
};

export const moveBetweenTrackingDocuments = async (
    fromId: string,
    toId: string,
    selectedRecords: Record[]
) => {
    return await runProcedure(
        MOVEDLVTRACKITEMS,
        updateMoveBetweenTrackingDocumentsInputFields(
            fromId,
            toId,
            selectedRecords.map(record =>
                cleanNumberString(
                    record.getField(record.parentForm.getColumnByName(DLVTRACKI).id)
                        .currentValue as string
                )
            )
        )
    );
};

export const updateMoveBetweenTrackingDocumentsInputFields =
    (fromId: string, toId: string, selectedRecordsIds: string[]) =>
    (inputFields: EditField[]): InputFieldsReturn => {
        const inputFieldsReturn = inputFields.map(field => {
            let value = field.value;

            switch (field.field) {
                case 1:
                    value = fromId;
                    break;
                case 2:
                    value = toId;
                    break;
                case 3:
                    value = selectedRecordsIds.join(' OR ');
            }
            return {
                field: field.field,
                op: 0,
                value: value,
                op2: 0,
                value2: value
            };
        });
        return {EditFields: inputFieldsReturn};
    };

export const moveToTrackingDocuments = async (toId: string, selectedRecords: Record[]) => {
    return await runProcedure(
        ADDTODLVTRACKITEMS,
        updateMoveToTrackingDocumentsInputFields(toId),
        null,
        () =>
            buildDataUrlFromParams(
                selectedRecords.map(record =>
                    cleanNumberString(
                        record.getField(record.parentForm.getColumnByName(DOCGUID).id)
                            .currentValue as string
                    )
                )
            )
    );
};

export const updateMoveToTrackingDocumentsInputFields =
    (toId: string) =>
    (inputFields: EditField[]): InputFieldsReturn => {
        const inputFieldsReturn = inputFields.map(field => {
            let value = field.value;

            switch (field.field) {
                case 1:
                    value = toId;
                    break;
            }
            return {
                field: field.field,
                op: 0,
                value: value,
                op2: 0,
                value2: value
            };
        });
        return {EditFields: inputFieldsReturn};
    };

export const changeDeliveryLocationsOrders = async (
    trackingDocument: string,
    locationsToMove: string[],
    afterLocation: string
) => {
    return await runProcedure(
        UPDDELIVERYORDER,
        updateUPDDELIVERYORDERInputFields(
            trackingDocument,
            locationsToMove.map(l => cleanNumberString(l)),
            cleanNumberString(afterLocation)
        )
    );
};

export const updateUPDDELIVERYORDERInputFields =
    (trackingDocument: string, locationsToMove: string[], afterLocation: string) =>
    (inputFields: EditField[]): InputFieldsReturn => {
        const inputFieldsReturn = inputFields.map(field => {
            let value = field.value;

            switch (field.field) {
                case 1:
                    value = trackingDocument;
                    break;
                case 2:
                    value = locationsToMove.join(' OR ');
                    break;
                case 3:
                    value = afterLocation;
                    break;
            }
            return {
                field: field.field,
                op: 0,
                value: value,
                op2: 0,
                value2: value
            };
        });
        return {EditFields: inputFieldsReturn};
    };

export const loadADPForm = async (
    onLoadSuccess: () => Promise<ResponseADP>
): Promise<ResponseADP> => {
    const procRes: ProcData = await startProcedure(LOADADVDISTRDATANOI, 'P');
    if (procRes.type === 'message' && (procRes as MessageProcData).messagetype === 'information') {
        return onLoadSuccess();
    } else {
        await processProcStepResult(procRes, defaultUpdateFunction);
        return null;
    }
};

export const startADPForms = async () => {
    const [planningForm, routesForm] = await Promise.all([openPlanningForm(), openRoutesForm()]);
    return {planningForm, routesForm};
};

export const openPlanningForm = async () => {
    const planningForm = await addNewStandAloneForm(
        ADVDISTRDATA,
        {},
        null,
        ADPFetchMax,
        [DOCGUID],
        {onErrorAccept: endProc}
    );
    if (!planningForm) {
        return null;
    }
    const isDefaultSearchApplied = await getSavedSearchesAction(planningForm, true);
    if (!isDefaultSearchApplied) {
        await fetchAllInTheBackground(planningForm);
    }
    return planningForm;
};

export const openRoutesForm = async () => {
    const routesForm = await addNewStandAloneForm(DLVTRACKS, {}, null, ADPFetchMax, [DLVTRACK], {
        onErrorAccept: endProc
    });
    if (!routesForm) {
        return null;
    }
    await getFormStatuses(routesForm);
    await getSavedSearchesAction(routesForm, true);
    return routesForm;
};

export const refetchInTheBackground = async (form: Form, fetchAllDefault: boolean = true) => {
    if (form.filters?.length > 0) {
        await applyFilters(
            {
                form,
                filtersOperator: priority.netitems.shared.LogicalOperator.And
            },
            true,
            true
        );
    } else if (fetchAllDefault) {
        await fetchAllInTheBackground(form);
    }
};

export const fetchAllInTheBackground = async (form: Form) => {
    await addAndApplyFilters(
        form,
        [
            {
                fieldId: form?.columns?.find(c => c.Type === priority.netitems.form.ColumnType.Str)
                    ?.id,
                value: '*'
            }
        ],
        ADPFetchMax,
        false
    );
};

export const processProcStepResult = async (procRes: ProcData, updateFunc) => {
    return new Promise(async (resolve, reject) => {
        const proc = procRes.proc;
        switch (procRes.type) {
            case 'inputFields':
                const input: InputFields = (procRes as InputProcData).input as InputFields;
                const inputFields: EditField[] = input.EditFields;
                const updatedInputFields: InputFieldsReturn = updateFunc(inputFields);
                const res = await proc.inputFields(1, updatedInputFields);
                resolve(processProcStepResult(res, updateFunc));
                break;
            case 'message':
                const msgRes = procRes as ProcBaseData & MessageProcData;
                if (msgRes.messagetype === 'information') {
                    showInfoMessage({content: msgRes.message});
                    const res = await proc.continueProc();
                    if (res.type === 'end') {
                        const magParts = msgRes.message.split(' ');
                        const parseRes = Number(magParts[0]);
                        if (isNaN(parseRes)) {
                            resolve(false);
                        } else {
                            resolve(true);
                        }
                    } else {
                        resolve(processProcStepResult(res, updateFunc));
                    }
                } else if (msgRes.messagetype === 'warning') {
                    const processProcContinue = async () => {
                        const res = await proc.continueProc();
                        resolve(processProcStepResult(res, updateFunc));
                    };
                    showConfirmMessage({
                        content: msgRes.message,
                        onConfirm: processProcContinue,
                        confirmButtonText: messagesText.yes,
                        cancleButtonText: messagesText.cancel,
                        defaultButton: 0
                    });
                } else if (msgRes.messagetype === 'error') {
                    showErrorMessage({
                        title: msgRes.message,
                        onConfirm: () => reject(),
                        onClose: () => reject(),
                        confirmButtonText: getLocalizedText(texts.confirmLabel)
                    });
                }
                break;
            case 'end':
                resolve(false);
                break;
            default:
                break;
        }
    });
};

export const updateStatus = async (form: Form, selectedRecords: RouteType[], status: string) => {
    for (let i = 0; i < selectedRecords.length; i++) {
        const record = selectedRecords[i];
        form.setCurrentRow(record.data.recordIndex);
        await updateCurrentRecordField(form, STATUS_FIELD, status);
        const screen = formsStore.getScreenBySession(form.session);
        const isOk = await screen.saveRecord();
        if (!isOk) {
            await undoAll(formsStore.getScreenBySession(form.session));
        }
    }
};

export const addDocumentsToDeliveryPlan = async (date: string, selectedRecords: Record[]) => {
    return await runProcedure(
        ADDDOCSTODELPLAN,
        updateAddDocsToDeliveryPlanInputFields(date),
        null,
        () =>
            buildDataUrlFromParams(
                selectedRecords.map(record =>
                    cleanNumberString(
                        record.getField(record.parentForm.getColumnByName(DOCGUID).id)
                            .currentValue as string
                    )
                )
            )
    );
};

export const updateAddDocsToDeliveryPlanInputFields =
    (date: string) =>
    (inputFields: EditField[]): InputFieldsReturn => {
        const inputFieldsReturn = inputFields.map(field => {
            let value = field.value;

            switch (field.field) {
                case 1:
                    value = date;
                    break;
                case 2:
                    value = date ? 'N' : 'Y';
                    break;
            }
            return {
                field: field.field,
                op: 0,
                value: value,
                op2: 0,
                value2: value
            };
        });
        return {EditFields: inputFieldsReturn};
    };

export const loadRouteWarehouses = async (route: RouteType) => {
    if (!route.startMarker) {
        const routesForm = route.data.parentForm;
        if (routesForm.records.length) {
            await startAndFetchSubform(routesForm, 'DLVSTARTROUTEADDRESS');
            const columnMapStart = mappedColumnIds(routesForm.activeSubForm.columns);
            if (routesForm.activeSubForm.records.length) {
                const latIdStart = columnMapStart['GPSX'];
                const latStart = routesForm.activeSubForm.records[0].getField(latIdStart).origValue;
                const lngIdStart = columnMapStart['GPSY'];
                const lngStart = routesForm.activeSubForm.records[0].getField(lngIdStart).origValue;
                route.startMarker = {
                    id: getUuid4(),
                    address: buildAddress(routesForm.activeSubForm.records[0], columnMapStart),
                    label: 'S',
                    selected: false,
                    lat: Number(latStart),
                    lng: Number(lngStart),
                    color: route?.color,
                    route: route,
                    show: true,
                    isStart: true
                };
            }
            await exitSubForm(routesForm.session);

            await startAndFetchSubform(routesForm, 'DLVENDROUTEADDRESS');
            const columnMapEnd = mappedColumnIds(routesForm.activeSubForm.columns);
            if (routesForm.activeSubForm.records.length) {
                const latIdEnd = columnMapEnd['GPSX'];
                const latEnd = routesForm.activeSubForm.records[0].getField(latIdEnd).origValue;
                const lngIdEnd = columnMapEnd['GPSY'];
                const lngEnd = routesForm.activeSubForm.records[0].getField(lngIdEnd).origValue;
                route.endMarker = {
                    id: getUuid4(),
                    address: buildAddress(routesForm.activeSubForm.records[0], columnMapEnd),
                    label: 'E',
                    selected: false,
                    lat: Number(latEnd),
                    lng: Number(lngEnd),
                    color: route?.color,
                    route: route,
                    show: true,
                    isEnd: true
                };
            }
            await exitSubForm(routesForm.session);
            formsStore.getScreenBySession(routesForm.session).setActiveLevel(0);
        }
    }
};
